import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Search, Sparkles, BadgeCheck, Globe2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const fmtFjd = (n) => {
    const num = Math.round(n || 0);
    return `FJ$${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(num)}`;
};

export default function MatchesDialog({ listing, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/agent/listings/${listing.id}/matches`);
                setData(res.data);
            } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Could not load matches");
            } finally {
                setLoading(false);
            }
        })();
    }, [listing.id]);

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="matches-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground flex items-center gap-2">
                        <Search className="h-4 w-4 text-fiji-green" /> Who's looking for this?
                    </DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Top investor matches from our watchlist for{" "}
                        <strong className="text-foreground">{listing.title}</strong>. Direct contact disabled —
                        click <em className="text-foreground">Inquire via Platform</em> to route a conversation safely.
                    </DialogDescription>
                </DialogHeader>
                {loading && (
                    <div className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-coral mx-auto" />
                    </div>
                )}
                {data && (
                    <div className="space-y-3 pt-2">
                        <div className="flex flex-wrap gap-3 text-xs font-app text-muted-foreground" data-testid="matches-stats">
                            <span><strong className="text-foreground">{data.total_in_pool}</strong> in pool</span>
                            <span>·</span>
                            <span><strong className="text-fiji-green">{data.qualified}</strong> qualified</span>
                            <span>·</span>
                            <span>Top <strong className="text-foreground">{data.matches.length}</strong> shown</span>
                        </div>
                        {data.matches.length === 0 ? (
                            <Card className="p-6 text-center bg-card">
                                <Globe2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                <p className="font-app text-sm text-muted-foreground">
                                    No matches yet. As more investors join the watchlist with matching criteria,
                                    they'll appear here automatically.
                                </p>
                            </Card>
                        ) : (
                            data.matches.map((m) => (
                                <Card key={m.anon_id} data-testid={`match-${m.anon_id}`} className="p-4 bg-card">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div>
                                            <div className="font-display text-sm font-bold text-foreground">
                                                Investor #{m.anon_id} · {m.country}
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {m.accredited && (
                                                    <Badge variant="outline" className="text-[10px] border-fiji-green/40 text-fiji-green">
                                                        <BadgeCheck className="h-2.5 w-2.5 mr-1" /> Accredited
                                                    </Badge>
                                                )}
                                                {m.phone_verified && (
                                                    <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">
                                                        Phone verified
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <Badge className="bg-fiji-green text-white shrink-0 tabular-nums">{m.match_score}%</Badge>
                                    </div>
                                    {(m.capital_min_fjd || m.capital_max_fjd) && (
                                        <div className="text-xs font-app text-muted-foreground mb-2">
                                            Capital range:{" "}
                                            <span className="text-foreground tabular-nums">
                                                {fmtFjd(m.capital_min_fjd || 0)} – {fmtFjd(m.capital_max_fjd || 0)}
                                            </span>
                                        </div>
                                    )}
                                    {(m.interests_top || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {m.interests_top.map((t) => (
                                                <Badge key={t} variant="outline" className="text-[10px] capitalize">{t}</Badge>
                                            ))}
                                        </div>
                                    )}
                                    {(m.reasons || []).length > 0 && (
                                        <ul className="list-none space-y-0.5 mt-1">
                                            {m.reasons.slice(0, 3).map((r) => (
                                                <li key={r} className="text-[11px] font-app text-muted-foreground flex items-center gap-1">
                                                    <Sparkles className="h-2.5 w-2.5 text-coral" /> {r}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Card>
                            ))
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button onClick={onClose} className="font-app bg-ocean-deep text-white hover:bg-ocean-deep/90">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
